import { filterVariables } from '@metservicedev/ncojs';
import { Button } from 'antd';
import { saveAs } from 'file-saver';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Zip from 'jszip';
import { useMDSContext } from '@metservice/use-mds';
import { DownloadOutlined } from '@ant-design/icons';
import { cfjsonToCsv } from '@metservicedev/cfjson-to-csv/src/cfjson-to-csv';

import { Data } from '../WidgetBlocks/types';
import IDatability from '../WidgetBlocks/types/IDatability';
import { toHash } from '@mono/to-hash/src/to-hash';
import { getDatabilityIndex } from '@metservicedev/platforms-services-interface/src';
import { useUserTracking } from '@mono/user-tracking';

interface DataDownloaderProps {
    zipName: string;
    datasetsWithData: Data[];
    databilities: IDatability[];
    databilityToUnit: { [key: string]: string | null };
    timezone?: string;
}

const unique = (arr: any[]) => [...new Set(arr)];

const ConsistentPositionDataDownloader = styled.div`
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 2;
`;

const DataDownloader = ({ databilities, datasetsWithData, zipName, databilityToUnit, timezone }: DataDownloaderProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const { capabilities } = useMDSContext();
    const { track } = useUserTracking();

    // csv is only 2 dimensional, and we use one dimension for variables and the other for time.
    // For this reason, we need separate files for each databilityIndex (databilityIndex ensures dimensions are equal)
    const datasetsByDatabilityIndex = datasetsWithData.reduce<{ [databilityIndex: string]: Data }>((prev, current) => {
        if (!current.databilityIndex) return prev;
        return { ...prev, [current.databilityIndex]: current };
    }, {});

    // dataForDownload is an array of objects that contain the datasetId, cfjson, and variableUnitMap for each databilityIndex
    // variableUnitMap is an object that maps each individual variable to multiple units.
    // This covers the case where a single variable may be represented in multiple units. (e.g. temperature in celsius and fahrenheit)
    const dataForDownload = useMemo(() => {
        return Object.keys(datasetsByDatabilityIndex)
            .map((databilityIndex) => {
                const datasetId = datasetsByDatabilityIndex[databilityIndex].datasetId;
                const cfjson = datasetsByDatabilityIndex[databilityIndex].cfjson;
                if (!cfjson || !datasetId) return null;

                const relevantDatabilities = databilities.filter((d) => getDatabilityIndex(d) === databilityIndex);

                const variableUnitMap = relevantDatabilities.reduce<{ [key: string]: string[] }>((prev, currentDatability) => {
                    const relevantCapability = capabilities.find((c) => c.id === currentDatability.capabilityId);
                    if (!relevantCapability) return prev;
                    const variables = Object.keys(relevantCapability.variables);

                    if (variables.length !== 1) return prev;
                    const variable = variables[0];

                    const unit = databilityToUnit[currentDatability.id];
                    if (!unit) return prev;

                    if (prev[variable]) {
                        return { ...prev, [variable]: unique([...prev[variable], unit]) };
                    }
                    const defaultVariable = relevantCapability.variables[variable];
                    return { ...prev, [variable]: unique([unit, ...(defaultVariable ? [defaultVariable.units] : [])]) };
                }, {});

                return { datasetId, cfjson: filterVariables(cfjson, Object.keys(variableUnitMap)), variableUnitMap, databilityIndex };
            })
            .filter((d): d is { datasetId: string; cfjson: any; variableUnitMap: { [key: string]: string[] }; databilityIndex: string } => !!d);
    }, [toHash([databilities, datasetsByDatabilityIndex])]);

    const downloadData = async () => {
        if (dataForDownload.length === 0) return console.warn('there was no data to download');
        track('forecast_map_actions', { action: 'csv_download', actionDetails: dataForDownload.map(({ datasetId }) => datasetId) });
        setIsLoading(true);

        const zip = new Zip();
        dataForDownload.forEach((datumForDownload) => {
            zip.file(
                datumForDownload.datasetId + '.csv',
                cfjsonToCsv({ cfjson: datumForDownload.cfjson, variableUnitMap: datumForDownload.variableUnitMap, timezone })
            );
        });
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, `mov-${zipName}-data.zip`);
        setIsLoading(false);
    };

    return (
        <ConsistentPositionDataDownloader>
            <Button icon={<DownloadOutlined />} type='primary' size='large' shape='round' onClick={downloadData} loading={isLoading}>
                csv
            </Button>
        </ConsistentPositionDataDownloader>
    );
};

export default DataDownloader;
